// Lawn Lime Calculator engine — a thin adapter over the production soil-test
// agronomy engine (lib/soil-test/agronomy-tables.ts). It exists so the standalone
// /tools/lime-calculator tool and the Soil Test Analyzer print IDENTICAL lime and
// sulfur numbers: both call the same `calculateLimeRecommendation` /
// `calculateSulfurRecommendation` / `GRASS_AGRONOMY` source of truth.
//
// The only new thing here is a consumer-facing translation the underlying engine
// doesn't have: our users don't know their CEC (that comes from a lab test), they
// only know rough soil texture. We map texture -> a representative CEC that lands
// in the engine's own `classifyCec` bucket, so the tested rate math runs untouched.
//
// This module is pure and has no React/Next dependency (region hint reads static
// data only), so it's trivially unit-testable.

import {
  GRASS_AGRONOMY,
  calculateLimeRecommendation,
  calculateSulfurRecommendation,
  totalProductLbsForLawn,
  cite,
  type SoilTextureClass,
} from '@/lib/soil-test/agronomy-tables'
import type { Citation, GrassKey } from '@/lib/soil-test/types'
import {
  getRegionFromZipCode,
  getRegionalClimateData,
} from '@/data/regional-climate'

export type MagnesiumLevel = 'adequate' | 'low' | 'unknown'
export type AmendmentDirection = 'lime' | 'sulfur' | 'none'

export interface LimeCalculatorInput {
  /** Measured soil pH from a strip/probe (3-10). */
  currentPh: number
  grassType: GrassKey
  /** Consumer-friendly soil texture; mapped to a representative CEC internally. */
  soilTexture: SoilTextureClass
  lawnSizeSqft: number
  /** Advanced: from a soil test if the user has one. Drives calcitic vs dolomitic. */
  magnesium?: MagnesiumLevel
  /** Advanced: 5-digit ZIP for a non-binding regional soil hint (not used in math). */
  zipCode?: string
}

export interface LimeCalculatorResult {
  direction: AmendmentDirection
  grassDisplayName: string
  currentPh: number
  /** The grass's ideal pH (the target the amendment aims for). */
  targetPh: number
  /** Below `phMin` -> lime; above `phMax` -> sulfur; inside -> in range. */
  phMin: number
  phMax: number
  inRange: boolean

  /** Human label for the amendment ("Pelletized lime" etc.). Null when in range. */
  amendment: string | null
  /** Which lime chemistry the engine prefers, when direction is 'lime'. */
  preferredSource: 'calcitic' | 'dolomitic' | null
  sourceReason: string | null

  lbsPer1000: number | null
  totalLbs: number | null
  bagLbs: number | null
  bags: number | null
  applications: number | null
  splitRequired: boolean
  splitNote: string | null

  timingNotes: string[]
  citations: Citation[]

  /** Copy for the "your pH is already right" success card. Null unless in range. */
  inRangeText: string | null
  /** Regional soil hint from ZIP, or null. Never affects the numbers. */
  regionHint: string | null
}

// Representative CEC (meq/100g) per soil texture, chosen to land inside the
// engine's own `classifyCec` buckets: sandy < 7, loam 7-15, clay > 15. This is
// how the consumer texture dropdown feeds the CEC-based rate math unchanged.
const TEXTURE_CEC: Record<SoilTextureClass, number> = {
  sandy: 5,
  loam: 11,
  clay: 20,
}

// Approximate magnesium ppm per self-reported level. `low` (< 60 ppm) trips the
// engine's dolomitic-lime preference; `adequate` stays calcitic; `unknown` passes
// null so the engine defaults to calcitic.
const MAGNESIUM_PPM: Record<MagnesiumLevel, number | null> = {
  adequate: 80,
  low: 40,
  unknown: null,
}

// Approximate retail bag sizes, used only for the "how many bags" reframe — the
// pound figures are the authoritative output. 40 lb is the most common pelletized
// lime bag; elemental sulfur is typically sold in smaller bags.
const LIME_BAG_LBS = 40
const SULFUR_BAG_LBS = 18

/** Build the non-binding regional soil hint from a ZIP, or null. */
function regionHintForZip(zipCode: string | undefined): string | null {
  if (!zipCode || !/^\d{5}$/.test(zipCode)) return null
  const region = getRegionFromZipCode(zipCode)
  if (region === 'Unknown') return null
  const data = getRegionalClimateData(zipCode)
  if (!data?.soilType) return null
  return `Soils in the ${region} region are typically ${data.soilType.toLowerCase()}. This is a general regional pattern and does not change your result — your pH reading is what matters.`
}

/**
 * Compute a lime/sulfur recommendation for a lawn from a pH strip reading.
 * Pure. Delegates all agronomy to lib/soil-test/agronomy-tables.ts.
 */
export function calculateLime(input: LimeCalculatorInput): LimeCalculatorResult {
  const { currentPh, grassType, soilTexture, lawnSizeSqft } = input
  const magnesium: MagnesiumLevel = input.magnesium ?? 'unknown'

  const grass = GRASS_AGRONOMY[grassType]
  const { min: phMin, ideal: targetPh, max: phMax } = grass.ph
  const cec = TEXTURE_CEC[soilTexture]
  const regionHint = regionHintForZip(input.zipCode)

  // "In range" is the grass's whole happy band, not just the ideal point — adding
  // an amendment inside the band would push pH the wrong way.
  const inRange = currentPh >= phMin && currentPh <= phMax

  const base = {
    grassDisplayName: grass.displayName,
    currentPh,
    targetPh,
    phMin,
    phMax,
    regionHint,
  }

  if (inRange) {
    return {
      ...base,
      direction: 'none',
      inRange: true,
      amendment: null,
      preferredSource: null,
      sourceReason: null,
      lbsPer1000: null,
      totalLbs: null,
      bagLbs: null,
      bags: null,
      applications: null,
      splitRequired: false,
      splitNote: null,
      timingNotes: [],
      citations: [],
      inRangeText: `At pH ${currentPh.toFixed(1)}, your soil sits inside ${grass.displayName}'s happy range of ${phMin.toFixed(1)}-${phMax.toFixed(1)}. Adding lime or sulfur now would push it out of range, so save the money and re-test in a couple of years.`,
    }
  }

  const direction: Exclude<AmendmentDirection, 'none'> =
    currentPh < phMin ? 'lime' : 'sulfur'

  if (direction === 'lime') {
    const rec = calculateLimeRecommendation(currentPh, targetPh, cec, MAGNESIUM_PPM[magnesium])
    // rec is non-null here because currentPh < phMin <= targetPh.
    if (!rec) return calculateLime({ ...input, currentPh: phMin - 0.1 }) // defensive, unreachable
    const totalLbs = totalProductLbsForLawn(rec.totalLbsPer1000, lawnSizeSqft)
    const bags = totalLbs != null ? Math.max(1, Math.ceil(totalLbs / LIME_BAG_LBS)) : null
    const dolomitic = rec.preferredSource === 'dolomitic'
    return {
      ...base,
      direction: 'lime',
      inRange: false,
      amendment: dolomitic ? 'Dolomitic lime' : 'Pelletized lime',
      preferredSource: rec.preferredSource,
      sourceReason: dolomitic
        ? `Dolomitic lime recommended: your magnesium is low, and dolomitic lime corrects both pH and magnesium in one pass.`
        : `Calcitic pelletized lime recommended: it raises pH without adding magnesium your soil doesn't need, and pellets spread cleanly in a broadcast spreader.`,
      lbsPer1000: rec.totalLbsPer1000,
      totalLbs,
      bagLbs: LIME_BAG_LBS,
      bags,
      applications: rec.applicationsRecommended,
      splitRequired: rec.splitRequired,
      splitNote: rec.splitRequired
        ? `That's more than 50 lbs per 1,000 sq ft, the safe single-application max for lime. Split it into ${rec.applicationsRecommended} passes of about ${rec.singleApplicationLbsPer1000} lbs per 1,000 sq ft, 4-6 months apart, re-testing between.`
        : null,
      timingNotes: [
        'Fall is the best time to lime: winter freeze-thaw cycles work it into the root zone, so pH is corrected by spring. Spring is second best.',
        "Water it in after spreading. Pelletized lime is grass-safe at these rates; no need to keep kids or pets off once it's watered.",
        'pH moves slowly. Expect the change over 3-6 months, and re-test before adding more.',
      ],
      citations: rec.citations.map(cite),
      inRangeText: null,
    }
  }

  // Sulfur (lower pH).
  const rec = calculateSulfurRecommendation(currentPh, targetPh, cec)
  if (!rec) return calculateLime({ ...input, currentPh: phMax + 0.1 }) // defensive, unreachable
  const totalLbs = totalProductLbsForLawn(rec.totalLbsPer1000, lawnSizeSqft)
  const bags = totalLbs != null ? Math.max(1, Math.ceil(totalLbs / SULFUR_BAG_LBS)) : null
  return {
    ...base,
    direction: 'sulfur',
    inRange: false,
    amendment: 'Elemental sulfur',
    preferredSource: null,
    sourceReason:
      'Use elemental sulfur (not aluminum sulfate) for lawns. Soil bacteria convert it slowly, which is far safer for roots than a fast chemical drop.',
    lbsPer1000: rec.totalLbsPer1000,
    totalLbs,
    bagLbs: SULFUR_BAG_LBS,
    bags,
    applications: rec.applicationsRecommended,
    splitRequired: rec.splitRequired,
    splitNote: rec.splitRequired
      ? `That's more than 10 lbs per 1,000 sq ft, the safe single-application max for sulfur. Split it into ${rec.applicationsRecommended} passes of about ${rec.singleApplicationLbsPer1000} lbs per 1,000 sq ft, a season apart, re-testing between.`
      : null,
    timingNotes: [
      'Apply sulfur in spring or fall when soil is moist and below 80°F. The soil bacteria that convert it work slowly.',
      'Never exceed 10 lbs per 1,000 sq ft in a single pass; more can burn the lawn.',
      'Lowering pH takes months per application. Re-test each season and repeat until you reach target.',
    ],
    citations: rec.citations.map(cite),
    inRangeText: null,
  }
}
